<template>
  <div class="inventory-page">
    <div class="inventory-page__heading">
      <div>
        <div class="inventory-page__eyebrow">CONTROL OPERATIVO</div>
        <h1>Inventory</h1>
        <p>Studio stock, ordering thresholds and count readiness in one place.</p>
      </div>
      <div v-if="canEdit" class="inventory-page__actions">
        <v-btn color="primary" prepend-icon="mdi-plus" @click="openItemDialog">
          Add Item
        </v-btn>
        <v-btn color="success" prepend-icon="mdi-package-down" @click="openRestockDialog">
          Restock
        </v-btn>
      </div>
    </div>

    <v-alert
      v-if="error"
      class="mb-4"
      type="warning"
      variant="tonal"
      closable
      @click:close="error = ''"
    >
      {{ error }}
    </v-alert>

    <v-tabs v-model="activeTab" class="inventory-tabs" color="primary">
      <v-tab value="overview">Overview</v-tab>
      <v-tab value="items">Items</v-tab>
      <v-tab value="counts">Count Sessions</v-tab>
      <v-tab value="sales">Sales History</v-tab>
      <v-tab value="history">Movement History</v-tab>
      <v-tab value="orders">Orders</v-tab>
    </v-tabs>

    <v-window v-model="activeTab" class="inventory-window">
      <v-window-item value="overview">
        <v-row>
          <v-col v-for="card in summaryCards" :key="card.label" cols="12" sm="6" lg="3">
            <v-card class="summary-card" elevation="0">
              <div class="summary-card__icon">
                <v-icon size="23">{{ card.icon }}</v-icon>
              </div>
              <div>
                <div class="summary-card__label">{{ card.label }}</div>
                <div class="summary-card__value">{{ card.value }}</div>
                <div class="summary-card__helper">{{ card.helper }}</div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-if="overview.attention.length" class="mt-1">
          <v-col cols="12">
            <v-card class="panel-card" elevation="0">
              <div class="panel-card__heading">
                <div>
                  <h2>Needs Attention</h2>
                  <p>Low-stock items and other actions will collect here.</p>
                </div>
                <v-chip size="small" color="warning" variant="tonal">
                  {{ overview.attention.length }} open
                </v-chip>
              </div>

              <div class="attention-list">
                <div v-for="item in overview.attention" :key="item.id" class="attention-row">
                  <div class="attention-row__indicator"></div>
                  <div class="attention-row__content">
                    <strong>{{ item.name }}</strong>
                    <span v-if="item.alert_type === 'low_stock'">{{ item.category_name || 'Uncategorized' }} · {{ item.location_name || 'No location' }}</span>
                    <span v-else-if="item.alert_type === 'negative_stock'">Recorded stock is below zero and needs reconciliation.</span>
                    <span v-else>Order #{{ item.order_id }} · {{ item.message }}</span>
                  </div>
                  <div v-if="['low_stock', 'negative_stock'].includes(item.alert_type)" class="attention-row__stock">
                    <strong>{{ item.quantity_on_hand }}</strong>
                    <span>{{ item.alert_type === 'negative_stock' ? 'recorded stock' : `minimum ${item.minimum_stock}` }}</span>
                  </div>
                  <v-btn size="small" variant="tonal" color="primary" @click="item.alert_type === 'negative_stock' ? goToCounts() : goToOrders()">
                    {{ item.alert_type === 'negative_stock' ? 'Review Count' : (item.alert_type === 'low_stock' ? 'Add to Order' : 'Review Order') }}
                  </v-btn>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <div v-else class="attention-clear mt-4">
          <v-icon size="22" color="success">mdi-check-circle-outline</v-icon>
          <strong>Nothing needs attention</strong>
          <span>Low stock, negative inventory, and order issues will appear here.</span>
        </div>

        <v-row class="mt-1">
          <v-col cols="12" lg="8">
            <v-card class="panel-card" elevation="0">
              <div class="panel-card__heading">
                <div>
                  <h2>Recent Sales</h2>
                  <p>Inventory sales in the selected period.</p>
                </div>
                <div class="recent-sales-actions">
                  <v-select
                    v-model="overviewSalesPeriodId"
                    :items="overviewPeriods"
                    class="recent-sales-period"
                    item-title="name"
                    item-value="id"
                    density="compact"
                    hide-details
                    variant="outlined"
                    aria-label="Recent sales period"
                    @update:modelValue="loadOverviewRecentSales"
                  />
                  <v-btn size="small" variant="text" color="primary" @click="activeTab = 'sales'">View All</v-btn>
                </div>
              </div>

              <div v-if="overviewSalesLoading" class="empty-panel empty-panel--compact">
                <v-progress-circular indeterminate size="25" width="2" color="primary" />
              </div>
              <div v-else-if="overview.recentSales.length" class="recent-sales-table-wrap">
                <div :class="['recent-sales-columns', { 'recent-sales-grid--no-cost': !canViewCosts }]">
                  <span>Buyer</span>
                  <span>Item</span>
                  <span class="number-cell">Qty</span>
                  <span class="number-cell">Price</span>
                  <span v-if="canViewCosts" class="number-cell">Cost</span>
                  <span v-if="canViewCosts" class="number-cell">Profit</span>
                </div>
                <div class="recent-sales-list">
                  <div v-for="sale in overview.recentSales" :key="sale.id" :class="['recent-sale-row', { 'recent-sales-grid--no-cost': !canViewCosts }]">
                    <strong class="recent-sale-row__buyer">{{ sale.stageName || sale.fullName || 'Model' }}</strong>
                    <strong class="recent-sale-row__item">{{ sale.item_name }}</strong>
                    <span class="recent-sale-row__quantity">{{ recentSaleQuantity(sale) }}</span>
                    <span class="recent-sale-row__money">{{ formatCurrency(sale.sale_total) }}</span>
                    <span v-if="canViewCosts" class="recent-sale-row__money">{{ recentSaleHasCost(sale) ? formatCurrency(recentSaleCost(sale)) : '—' }}</span>
                    <span v-if="canViewCosts" class="recent-sale-row__money recent-sale-row__profit">{{ recentSaleHasCost(sale) ? formatCurrency(recentSaleProfit(sale)) : '—' }}</span>
                  </div>
                </div>
                <div :class="['recent-sales-total', { 'recent-sales-grid--no-cost': !canViewCosts }]">
                  <strong>Period total</strong>
                  <span></span>
                  <strong class="number-cell">{{ overviewSalesTotals.quantity }}</strong>
                  <strong class="number-cell">{{ formatCurrency(overviewSalesTotals.sales) }}</strong>
                  <strong v-if="canViewCosts" class="number-cell">{{ overviewSalesTotals.completeCosts ? formatCurrency(overviewSalesTotals.cost) : '—' }}</strong>
                  <strong v-if="canViewCosts" class="number-cell recent-sale-row__profit">{{ overviewSalesTotals.completeCosts ? formatCurrency(overviewSalesTotals.profit) : '—' }}</strong>
                </div>
              </div>
              <div v-else class="empty-panel empty-panel--compact">
                <v-icon size="28" color="primary">mdi-cart-outline</v-icon>
                <div>
                  <strong>No inventory sales yet</strong>
                  <p>Approved model purchases will appear here.</p>
                </div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" lg="4">
            <v-card class="panel-card" elevation="0">
              <div class="panel-card__heading">
                <div>
                  <h2>Recent Movement</h2>
                  <p>The latest audited stock changes.</p>
                </div>
              </div>

              <div v-if="overview.recentMovements.length" class="movement-list">
                <div v-for="movement in overview.recentMovements" :key="movement.id" class="movement-row">
                  <div>
                    <strong>{{ movement.item_name }}</strong>
                    <span>{{ formatMovementType(movement.movement_type) }}</span>
                  </div>
                  <div :class="['movement-row__quantity', Number(movement.quantity_change) >= 0 ? 'positive' : 'negative']">
                    {{ Number(movement.quantity_change) >= 0 ? '+' : '' }}{{ movement.quantity_change }}
                  </div>
                </div>
              </div>

              <div v-else class="empty-panel empty-panel--compact">
                <v-icon size="28" color="primary">mdi-swap-horizontal</v-icon>
                <div>
                  <strong>No movements yet</strong>
                  <p>Opening stock will be recorded when the first item is added.</p>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>

      <v-window-item value="items">
        <v-card class="panel-card" elevation="0">
          <div class="items-toolbar">
            <div>
              <h2>Studio Items</h2>
              <p>Locations and warning levels are specific to this studio.</p>
            </div>
            <div class="items-toolbar__controls">
              <v-select
                v-model="groupBy"
                :items="groupOptions"
                class="items-group-select"
                density="compact"
                :disabled="arrangeMode"
                hide-details
                item-title="title"
                item-value="value"
                label="Organize by"
                variant="outlined"
                @update:modelValue="handleGroupChange"
              ></v-select>
              <v-text-field
                v-model="search"
                class="items-search"
                density="compact"
                :disabled="arrangeMode"
                hide-details
                prepend-inner-icon="mdi-magnify"
                placeholder="Search items, categories or locations"
                variant="outlined"
                @update:modelValue="scheduleSearch"
              ></v-text-field>
              <v-btn
                v-if="canEdit && groupBy === 'location' && !arrangeMode"
                color="primary"
                prepend-icon="mdi-order-bool-ascending-variant"
                variant="tonal"
                @click="beginArrange"
              >
                Arrange Items
              </v-btn>
              <template v-if="arrangeMode">
                <v-btn variant="text" @click="cancelArrange">Cancel</v-btn>
                <v-btn color="primary" :loading="savingOrder" prepend-icon="mdi-content-save-outline" @click="saveItemOrder">
                  Save Order
                </v-btn>
              </template>
            </div>
          </div>

          <div class="items-table-wrap">
            <table class="items-table">
              <thead>
                <tr>
                  <th>
                    <button class="table-sort" type="button" @click="setSort('name')">
                      Item
                      <v-icon v-if="sortKey === 'name'" size="15">{{ sortIcon }}</v-icon>
                    </button>
                  </th>
                  <th>
                    <button class="table-sort" type="button" @click="setSort('category_name')">
                      Category
                      <v-icon v-if="sortKey === 'category_name'" size="15">{{ sortIcon }}</v-icon>
                    </button>
                  </th>
                  <th>
                    <button class="table-sort" type="button" @click="setSort('location_name')">
                      Location
                      <v-icon v-if="sortKey === 'location_name'" size="15">{{ sortIcon }}</v-icon>
                    </button>
                  </th>
                  <th class="number-cell stock-cell">Stock</th>
                  <th v-if="canViewCosts" class="number-cell">Cost</th>
                  <th class="number-cell">Selling Price</th>
                  <th class="number-cell">Minimum</th>
                  <th v-if="canEdit">Actions</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="row in tableRows" :key="row.key">
                  <tr v-if="row.type === 'group'" class="items-table__group-row">
                    <td
                      :colspan="itemTableColumnCount"
                      :class="{ 'items-table__group-drop-target': arrangeMode && dragOverLocationKey === locationKey(row.locationId) }"
                      @dragover.prevent="handleLocationDragOver($event, row.locationId)"
                      @drop.prevent="dropOnLocation(row.locationId)"
                    >
                      <div class="items-table__group-label">
                        <v-icon size="17">{{ groupBy === 'category' ? 'mdi-tag-outline' : 'mdi-map-marker-outline' }}</v-icon>
                        <strong>{{ row.label }}</strong>
                        <span>{{ row.count }} {{ row.count === 1 ? 'item' : 'items' }}</span>
                        <div v-if="canEdit && groupBy === 'location' && row.locationId" class="location-order-actions">
                          <v-btn
                            :aria-label="`Move ${row.label} up`"
                            :disabled="savingLocationOrder || !canMoveLocation(row.locationId, -1)"
                            icon="mdi-arrow-up"
                            size="x-small"
                            variant="text"
                            @click="moveLocation(row.locationId, -1)"
                          ></v-btn>
                          <v-btn
                            :aria-label="`Move ${row.label} down`"
                            :disabled="savingLocationOrder || !canMoveLocation(row.locationId, 1)"
                            icon="mdi-arrow-down"
                            size="x-small"
                            variant="text"
                            @click="moveLocation(row.locationId, 1)"
                          ></v-btn>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-else
                    :class="{
                      'items-table__item-row--alternate': row.itemIndex % 2 === 1,
                      'items-table__item-row--arranging': arrangeMode,
                      'items-table__item-row--dragging': Number(draggedItemId) === Number(row.item.id),
                      'items-table__item-row--drag-over': Number(dragOverItemId) === Number(row.item.id),
                    }"
                    :draggable="arrangeMode"
                    @dragstart="startDrag($event, row.item)"
                    @dragover.prevent="handleItemDragOver($event, row.item)"
                    @drop.prevent="dropItem(row.item)"
                    @dragend="endDrag"
                  >
                    <td>
                      <strong>{{ row.item.name }}</strong>
                      <span v-if="row.item.description">{{ row.item.description }}</span>
                    </td>
                    <td>{{ row.item.category_name || '—' }}</td>
                    <td>{{ row.item.location_name || '—' }}</td>
                    <td class="number-cell stock-cell">
                      <v-chip
                        v-if="isLowStock(row.item)"
                        color="warning"
                        size="small"
                        variant="tonal"
                      >
                        {{ row.item.quantity_on_hand }}
                      </v-chip>
                      <span v-else>{{ row.item.quantity_on_hand }}</span>
                    </td>
                    <td v-if="canViewCosts" class="number-cell">{{ formatCurrency(row.item.average_unit_cost) }}</td>
                    <td class="number-cell">{{ formatCurrency(row.item.default_selling_price) }}</td>
                    <td class="number-cell">{{ row.item.minimum_stock ?? '—' }}</td>
                    <td v-if="canEdit">
                      <div v-if="arrangeMode" class="item-actions item-actions--arrange">
                        <v-icon class="drag-handle" size="20">mdi-drag-horizontal-variant</v-icon>
                        <v-btn
                          :aria-label="`Move ${row.item.name} to the top of ${row.item.location_name || 'this location'}`"
                          :title="`Move to top of ${row.item.location_name || 'this location'}`"
                          :disabled="!canMoveItem(row.item, -1)"
                          icon="mdi-arrow-collapse-up"
                          size="x-small"
                          variant="text"
                          @click="moveItem(row.item, -1)"
                        ></v-btn>
                        <v-btn
                          :aria-label="`Move ${row.item.name} to the bottom of ${row.item.location_name || 'this location'}`"
                          :title="`Move to bottom of ${row.item.location_name || 'this location'}`"
                          :disabled="!canMoveItem(row.item, 1)"
                          icon="mdi-arrow-collapse-down"
                          size="x-small"
                          variant="text"
                          @click="moveItem(row.item, 1)"
                        ></v-btn>
                      </div>
                      <div v-else class="item-actions">
                        <v-btn size="small" variant="text" prepend-icon="mdi-pencil-outline" @click="openEditDialog(row.item)">Edit</v-btn>
                        <v-btn size="small" variant="text" prepend-icon="mdi-counter" @click="openStockDialog(row.item)">Stock</v-btn>
                      </div>
                    </td>
                  </tr>
                </template>
                <tr v-if="!tableRows.length && !loading">
                  <td :colspan="itemTableColumnCount" class="items-table__empty">
                    No inventory items yet. Add the first item to begin.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </v-card>
      </v-window-item>

      <v-window-item value="counts">
        <InventoryCounts
          :categories="categories"
          :can-edit="canEdit"
          :locations="locations"
          :refresh-key="workflowRefreshKey"
          :studio-id="currentStudioId"
          @changed="handleWorkflowChange"
        />
      </v-window-item>

      <v-window-item value="sales">
        <InventorySalesHistory :refresh-key="workflowRefreshKey" :show-costs="canViewCosts" />
      </v-window-item>

      <v-window-item value="history">
        <InventoryHistory
          :can-edit="canEdit"
          :refresh-key="workflowRefreshKey"
          :show-costs="canViewCosts"
          @edit-restock="openEditRestockDialog"
        />
      </v-window-item>

      <v-window-item value="orders">
        <InventoryOrders :items="items" :refresh-key="workflowRefreshKey" :show-costs="canViewCosts" :can-edit="canEdit" @changed="handleWorkflowChange" />
      </v-window-item>
    </v-window>

    <v-dialog v-model="itemDialog" max-width="720">
      <v-card>
        <v-card-title class="dialog-title">{{ editingItemId ? 'Edit Inventory Item' : 'Add Inventory Item' }}</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" :sm="editingItemId ? 12 : 8">
              <v-text-field v-if="editingItemId" v-model="itemForm.name" label="Item name" variant="outlined"></v-text-field>
              <v-combobox
                v-else
                v-model="itemForm.name"
                :items="restockOptions"
                :loading="loadingRestockOptions"
                item-title="name"
                item-value="name"
                :return-object="false"
                label="Item name"
                clearable
                variant="outlined"
              >
                <template #item="{ props, item }">
                  <v-list-item v-bind="props">
                    <template #subtitle>{{ restockOptionSubtitle(item.raw) }}</template>
                  </v-list-item>
                </template>
              </v-combobox>
            </v-col>
            <v-col v-if="!editingItemId" cols="12" sm="4">
              <v-text-field v-model.number="itemForm.openingQuantity" label="Opening stock" min="0" type="number" variant="outlined"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="itemForm.categoryId"
                :items="categories"
                item-title="name"
                item-value="id"
                label="Category"
                clearable
                variant="outlined"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="itemForm.locationId"
                :items="locations"
                item-title="name"
                item-value="id"
                label="Location"
                clearable
                variant="outlined"
              ></v-select>
            </v-col>
            <v-col v-if="canViewCosts" cols="12" sm="6">
              <v-text-field
                v-model.number="itemForm.unitCost"
                :hint="editingItemId ? 'Used for stock value and profit calculations' : undefined"
                :label="editingItemId ? 'Average unit cost' : 'Unit cost'"
                min="0"
                :persistent-hint="Boolean(editingItemId)"
                prefix="$"
                type="number"
                variant="outlined"
                @wheel="$event.target.blur()"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model.number="itemForm.sellingPrice" label="Selling price" min="0" prefix="$" type="number" variant="outlined"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model.number="itemForm.minimumStock" hint="Optional" label="Minimum stock" min="0" persistent-hint type="number" variant="outlined"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model.number="itemForm.reorderQuantity" hint="Optional suggested order" label="Reorder quantity" min="0" persistent-hint type="number" variant="outlined"></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="itemForm.description" label="Notes" rows="2" variant="outlined"></v-textarea>
            </v-col>
          </v-row>
          <v-alert
            v-if="!editingItemId && selectedExistingAddItem"
            class="mb-3"
            color="warning"
            icon="mdi-package-variant-closed"
            variant="tonal"
          >
            <div class="d-flex align-center justify-space-between flex-wrap ga-2">
              <div>
                <strong>{{ selectedExistingAddItem.name }} already exists</strong>
                <div>{{ restockOptionSubtitle(selectedExistingAddItem) }}</div>
              </div>
              <v-btn color="success" prepend-icon="mdi-package-variant-plus" variant="flat" @click="switchAddToRestock">
                Restock Existing Item
              </v-btn>
            </div>
          </v-alert>
          <div class="dialog-quick-links">
            <v-btn size="small" variant="text" prepend-icon="mdi-tag-plus-outline" @click="openLookupDialog('category')">New category</v-btn>
            <v-btn size="small" variant="text" prepend-icon="mdi-map-marker-plus-outline" @click="openLookupDialog('location')">New location</v-btn>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="editingItemId"
            color="error"
            prepend-icon="mdi-delete-outline"
            variant="text"
            :loading="deletingItem"
            @click="deleteItem"
          >Delete Item</v-btn>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="itemDialog = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="deletingItem" :loading="saving" @click="saveItem">{{ editingItemId ? 'Save Changes' : 'Save Item' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="stockDialog" max-width="480">
      <v-card>
        <v-card-title class="dialog-title">Adjust Stock</v-card-title>
        <v-card-text>
          <div class="stock-dialog__item">
            <strong>{{ stockForm.itemName }}</strong>
            <span>Current stock: {{ stockForm.currentQuantity }}</span>
          </div>
          <v-text-field
            v-model.number="stockForm.quantity"
            label="Correct stock quantity"
            min="0"
            type="number"
            variant="outlined"
          ></v-text-field>
          <v-text-field
            v-model="stockForm.reason"
            label="Reason"
            variant="outlined"
          ></v-text-field>
          <v-textarea
            v-model="stockForm.notes"
            label="Notes (optional)"
            rows="2"
            variant="outlined"
          ></v-textarea>
          <v-alert type="info" variant="tonal">
            The difference will be saved in Movement History instead of silently replacing the old count.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="stockDialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="savingStock" @click="saveStockAdjustment">Save Stock</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="restockDialog" max-width="560">
      <v-card>
        <v-card-title class="dialog-title">{{ editingRestockMovementId ? 'Edit Restock' : 'Restock Existing Item' }}</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="restockForm.itemId"
            :items="restockOptions"
            :loading="loadingRestockOptions"
            :disabled="Boolean(editingRestockMovementId)"
            autofocus
            clearable
            item-title="name"
            item-value="id"
            label="Item"
            no-data-text="No matching inventory item"
            variant="outlined"
            @update:modelValue="handleRestockItemChange"
          >
            <template #item="{ props, item }">
              <v-list-item v-bind="props">
                <template #subtitle>
                  {{ restockOptionSubtitle(item.raw) }}
                </template>
              </v-list-item>
            </template>
          </v-autocomplete>

          <div v-if="selectedRestockItem" class="stock-dialog__item restock-dialog__summary">
            <strong>{{ selectedRestockItem.name }}</strong>
            <span>
              Current stock: {{ selectedRestockItem.quantity_on_hand }}
              <template v-if="selectedRestockItem.location_name"> · {{ selectedRestockItem.location_name }}</template>
              <template v-if="!isRestockItemActive(selectedRestockItem)"> · Archived item will be reactivated</template>
            </span>
          </div>

          <v-row>
            <v-col cols="12" :sm="canViewCosts ? 4 : 6">
              <v-text-field
                v-model.number="restockForm.receivedQuantity"
                label="Quantity received"
                :disabled="Boolean(editingRestockMovementId) && !restockQuantityEditable"
                :hint="editingRestockMovementId && !restockQuantityEditable ? 'Locked by a later physical count' : ''"
                min="1"
                :persistent-hint="Boolean(editingRestockMovementId) && !restockQuantityEditable"
                type="number"
                variant="outlined"
                @wheel="$event.target.blur()"
              ></v-text-field>
            </v-col>
            <v-col v-if="canViewCosts" cols="12" sm="4">
              <v-text-field
                v-model="restockForm.unitCost"
                hint="Optional"
                label="Unit cost"
                min="0"
                persistent-hint
                prefix="$"
                type="number"
                variant="outlined"
                @wheel="$event.target.blur()"
              ></v-text-field>
            </v-col>
            <v-col cols="12" :sm="canViewCosts ? 4 : 6">
              <v-text-field
                v-model="restockForm.sellingPrice"
                hint="Current price shown"
                label="Selling price"
                min="0"
                persistent-hint
                prefix="$"
                type="number"
                variant="outlined"
                @wheel="$event.target.blur()"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="restockForm.notes"
                label="Notes (optional)"
                rows="2"
                variant="outlined"
              ></v-textarea>
            </v-col>
          </v-row>

          <v-alert :type="editingRestockMovementId ? 'info' : 'success'" variant="tonal">
            {{ editingRestockMovementId
              ? 'Saving updates this receipt and recalculates the current rolling average cost.'
              : 'The received quantity will be added to the current stock and saved as a receipt in Movement History.' }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeRestockDialog">Cancel</v-btn>
          <v-btn :color="editingRestockMovementId ? 'primary' : 'success'" :loading="savingRestock" @click="saveRestock">
            {{ editingRestockMovementId ? 'Save Changes' : 'Add Stock' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="lookupDialog" max-width="420">
      <v-card>
        <v-card-title class="dialog-title">New {{ lookupType === 'category' ? 'Category' : 'Location' }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="lookupName"
            autofocus
            :label="lookupType === 'category' ? 'Category name' : 'Location name'"
            variant="outlined"
            @keyup.enter="saveLookup"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="lookupDialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="savingLookup" @click="saveLookup">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-btn
      v-if="canEdit && arrangeMode"
      class="inventory-floating-save"
      color="primary"
      prepend-icon="mdi-content-save"
      size="large"
      :loading="savingOrder"
      @click="saveItemOrder"
    >
      Save Order
    </v-btn>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="2800">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import secureAxios from '@/utils/secureAxios';
import InventoryOrders from '@/components/inventory/InventoryOrders.vue';
import InventoryCounts from '@/components/inventory/InventoryCounts.vue';
import InventorySalesHistory from '@/components/inventory/InventorySalesHistory.vue';
import InventoryHistory from '@/components/inventory/InventoryHistory.vue';

const emptyOverview = () => ({
  summary: {
    activeItems: 0,
    unitsOnHand: 0,
    stockValue: 0,
    lowStockItems: 0,
    pendingOrders: 0,
    awaitingReceipt: 0,
  },
  attention: [],
  recentSales: [],
  recentMovements: [],
});

const emptyItemForm = () => ({
  name: '',
  description: '',
  categoryId: null,
  locationId: null,
  openingQuantity: 1,
  unitCost: 0,
  sellingPrice: 0,
  minimumStock: null,
  reorderQuantity: null,
});

export default {
  name: 'InventoryPage',
  components: { InventoryOrders, InventoryCounts, InventorySalesHistory, InventoryHistory },
  data() {
    return {
      activeTab: 'overview',
      overview: emptyOverview(),
      overviewPeriods: [],
      overviewSalesPeriodId: null,
      overviewSalesLoading: false,
      overviewSalesSummary: { sales: 0, cost: 0, profit: 0, quantity: 0, missingCostSales: 0 },
      items: [],
      categories: [],
      locations: [],
      loading: false,
      saving: false,
      error: '',
      search: '',
      searchTimer: null,
      groupBy: 'location',
      sortKey: 'location_name',
      sortDirection: 'asc',
      arrangeMode: false,
      savingOrder: false,
      savingLocationOrder: false,
      arrangeSnapshot: {},
      draggedItemId: null,
      dragOverItemId: null,
      dragOverLocationKey: null,
      groupOptions: [
        { title: 'Category', value: 'category' },
        { title: 'Location', value: 'location' },
        { title: 'No grouping', value: 'none' },
      ],
      itemDialog: false,
      editingItemId: null,
      deletingItem: false,
      itemForm: emptyItemForm(),
      stockDialog: false,
      savingStock: false,
      stockForm: { itemId: null, itemName: '', currentQuantity: 0, quantity: 0, reason: 'Manual stock adjustment', notes: '' },
      restockDialog: false,
      loadingRestockOptions: false,
      savingRestock: false,
      restockOptions: [],
      restockForm: { itemId: null, receivedQuantity: 1, unitCost: '', sellingPrice: '', notes: '' },
      editingRestockMovementId: null,
      restockQuantityEditable: true,
      lookupDialog: false,
      lookupType: 'category',
      lookupName: '',
      savingLookup: false,
      snackbar: { show: false, message: '', color: 'primary' },
      workflowRefreshKey: 0,
    };
  },
  computed: {
    currentStudioId() {
      try {
        return Number(JSON.parse(localStorage.getItem('user') || '{}')?.studio_id) || null;
      } catch {
        return null;
      }
    },
    canEdit() {
      return this.canViewCosts;
    },
    canViewCosts() {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return user?.role === 'admin' || user?.isAdmin === true;
      } catch {
        return false;
      }
    },
    itemTableColumnCount() {
      return 6 + (this.canViewCosts ? 1 : 0) + (this.canEdit ? 1 : 0);
    },
    summaryCards() {
      const summary = this.overview.summary;
      const cards = [
        { label: 'Active Items', value: summary.activeItems, helper: `${summary.unitsOnHand} total units`, icon: 'mdi-package-variant-closed' },
        { label: 'Stock Value', value: this.formatCurrency(summary.stockValue), helper: 'At current average cost', icon: 'mdi-cash-multiple' },
        { label: 'Low Stock', value: summary.lowStockItems, helper: 'At or below minimum', icon: 'mdi-alert-outline' },
        { label: 'Open Orders', value: summary.pendingOrders + summary.awaitingReceipt, helper: `${summary.awaitingReceipt} awaiting receipt`, icon: 'mdi-clipboard-text-outline' },
      ];
      return this.canViewCosts ? cards : cards.filter(card => card.label !== 'Stock Value');
    },
    sortIcon() {
      return this.sortDirection === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down';
    },
    selectedRestockItem() {
      return this.restockOptions.find(item => Number(item.id) === Number(this.restockForm.itemId)) || null;
    },
    selectedExistingAddItem() {
      if (this.editingItemId) return null;
      const wantedName = this.normalizeItemName(this.itemForm.name);
      if (!wantedName) return null;
      return this.restockOptions.find(item => this.normalizeItemName(item.name) === wantedName) || null;
    },
    overviewSalesTotals() {
      const summary = this.overviewSalesSummary || {};
      return {
        quantity: Number(summary.quantity || 0),
        sales: Number(summary.sales || 0),
        cost: Number(summary.cost || 0),
        profit: Number(summary.profit || 0),
        completeCosts: Number(summary.missingCostSales || 0) === 0,
      };
    },
    tableRows() {
      const sortedItems = [...this.items].sort((left, right) => {
        if (this.groupBy === 'location' && (this.arrangeMode || this.sortKey === 'location_name')) {
          const locationComparison = this.locationSortOrder(left.location_id) - this.locationSortOrder(right.location_id);
          if (locationComparison !== 0) return locationComparison;
          const orderComparison = Number(left.display_order || 0) - Number(right.display_order || 0);
          if (orderComparison !== 0) return orderComparison;
          return String(left.name || '').localeCompare(String(right.name || ''), undefined, { sensitivity: 'base', numeric: true });
        }
        return this.compareItems(left, right);
      });
      if (this.groupBy === 'none') {
        return sortedItems.map((item, itemIndex) => ({ type: 'item', key: `item-${item.id}`, item, itemIndex }));
      }

      const field = this.groupBy === 'category' ? 'category_name' : 'location_name';
      const fallback = this.groupBy === 'category' ? 'Uncategorized' : 'No location';
      const grouped = new Map();
      sortedItems.forEach(item => {
        const label = item[field] || fallback;
        if (!grouped.has(label)) grouped.set(label, []);
        grouped.get(label).push(item);
      });

      const groupDirection = this.sortKey === field && this.sortDirection === 'desc' ? -1 : 1;
      let itemIndex = 0;
      return [...grouped.entries()]
        .sort(([left, leftItems], [right, rightItems]) => {
          if (this.groupBy === 'location') {
            const leftOrder = this.locationSortOrder(leftItems[0]?.location_id);
            const rightOrder = this.locationSortOrder(rightItems[0]?.location_id);
            if (leftOrder !== rightOrder) return leftOrder - rightOrder;
          }
          return left.localeCompare(right, undefined, { sensitivity: 'base' }) * groupDirection;
        })
        .flatMap(([label, groupItems]) => [
          {
            type: 'group',
            key: `group-${this.groupBy}-${label}`,
            label,
            count: groupItems.length,
            locationId: this.groupBy === 'location' ? groupItems[0]?.location_id : null,
          },
          ...groupItems.map(item => ({ type: 'item', key: `item-${item.id}`, item, itemIndex: itemIndex++ })),
        ]);
    },
  },
  mounted() {
    this.loadInventory();
  },
  beforeUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    async loadInventory() {
      this.loading = true;
      this.error = '';
      try {
        const [overviewResponse, itemsResponse, categoriesResponse, locationsResponse, periodsResponse] = await Promise.all([
          secureAxios.get('/api/inventory/overview'),
          secureAxios.get('/api/inventory/items'),
          secureAxios.get('/api/inventory/categories'),
          secureAxios.get('/api/inventory/locations'),
          secureAxios.get('/api/hours/periods'),
        ]);
        this.overview = overviewResponse.data || emptyOverview();
        this.items = itemsResponse.data || [];
        this.categories = categoriesResponse.data || [];
        this.locations = locationsResponse.data || [];
        this.overviewPeriods = periodsResponse.data || [];
        if (!this.overviewSalesPeriodId || !this.overviewPeriods.some(
          period => Number(period.id) === Number(this.overviewSalesPeriodId)
        )) {
          this.overviewSalesPeriodId = this.currentInventoryPeriod()?.id || this.overviewPeriods[0]?.id || null;
        }
        if (this.overviewSalesPeriodId) await this.loadOverviewRecentSales();
      } catch (error) {
        this.error = error.response?.data?.message || 'Unable to load inventory.';
      } finally {
        this.loading = false;
      }
    },
    async loadItems() {
      try {
        const response = await secureAxios.get('/api/inventory/items', {
          params: { search: this.search || undefined },
        });
        this.items = response.data || [];
      } catch (error) {
        this.error = error.response?.data?.message || 'Unable to search inventory.';
      }
    },
    scheduleSearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.loadItems(), 250);
    },
    compareItems(left, right) {
      const direction = this.sortDirection === 'asc' ? 1 : -1;
      const leftValue = String(left[this.sortKey] || '');
      const rightValue = String(right[this.sortKey] || '');
      const comparison = leftValue.localeCompare(rightValue, undefined, { sensitivity: 'base', numeric: true });
      if (comparison !== 0) return comparison * direction;
      return String(left.name || '').localeCompare(String(right.name || ''), undefined, { sensitivity: 'base', numeric: true });
    },
    setSort(key) {
      if (this.arrangeMode) return;
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDirection = 'asc';
      }
    },
    handleGroupChange(value) {
      if (value === 'category') this.sortKey = 'category_name';
      if (value === 'location') this.sortKey = 'location_name';
      if (value === 'none' && !['name', 'category_name', 'location_name'].includes(this.sortKey)) this.sortKey = 'name';
      this.sortDirection = 'asc';
    },
    orderedLocations() {
      return [...this.locations].sort((left, right) => {
        const orderComparison = Number(left.sort_order || 0) - Number(right.sort_order || 0);
        if (orderComparison !== 0) return orderComparison;
        return String(left.name || '').localeCompare(String(right.name || ''), undefined, { sensitivity: 'base' });
      });
    },
    locationSortOrder(locationId) {
      if (locationId === null || locationId === undefined) return Number.MAX_SAFE_INTEGER;
      const index = this.orderedLocations().findIndex(location => Number(location.id) === Number(locationId));
      return index < 0 ? Number.MAX_SAFE_INTEGER : index;
    },
    canMoveLocation(locationId, direction) {
      const locations = this.orderedLocations();
      const index = locations.findIndex(location => Number(location.id) === Number(locationId));
      const nextIndex = index + direction;
      return index >= 0 && nextIndex >= 0 && nextIndex < locations.length;
    },
    async moveLocation(locationId, direction) {
      if (this.savingLocationOrder) return;
      const locations = this.orderedLocations();
      const index = locations.findIndex(location => Number(location.id) === Number(locationId));
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= locations.length) return;
      [locations[index], locations[nextIndex]] = [locations[nextIndex], locations[index]];
      locations.forEach((location, locationIndex) => {
        location.sort_order = (locationIndex + 1) * 10;
      });
      this.locations = locations;
      this.savingLocationOrder = true;
      try {
        const response = await secureAxios.put('/api/inventory/locations/display-order', {
          locationIds: locations.map(location => Number(location.id)),
        });
        this.notify(response.data?.message || 'Location order saved', 'success');
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to save location order', 'error');
        await this.loadInventory();
      } finally {
        this.savingLocationOrder = false;
      }
    },
    sameLocation(left, right) {
      return (left === null || left === undefined ? null : Number(left))
        === (right === null || right === undefined ? null : Number(right));
    },
    locationItems(item) {
      return this.items
        .filter(candidate => this.sameLocation(candidate.location_id, item.location_id))
        .sort((left, right) => {
          const orderComparison = Number(left.display_order || 0) - Number(right.display_order || 0);
          if (orderComparison !== 0) return orderComparison;
          return String(left.name || '').localeCompare(String(right.name || ''), undefined, { sensitivity: 'base', numeric: true });
        });
    },
    normalizeLocationOrder(items) {
      items.forEach((item, index) => {
        item.display_order = index + 1;
      });
    },
    beginArrange() {
      if (this.search.trim()) {
        return this.notify('Clear the search before arranging a complete location.', 'warning');
      }
      this.arrangeSnapshot = Object.fromEntries(this.items.map(item => [item.id, {
        displayOrder: Number(item.display_order || 0),
        locationId: item.location_id ?? null,
        locationName: item.location_name ?? null,
      }]));
      const handledLocations = [];
      this.items.forEach(item => {
        const locationKey = item.location_id === null || item.location_id === undefined ? 'none' : String(item.location_id);
        if (handledLocations.includes(locationKey)) return;
        handledLocations.push(locationKey);
        this.normalizeLocationOrder(this.locationItems(item));
      });
      this.arrangeMode = true;
      this.draggedItemId = null;
      this.dragOverItemId = null;
      this.dragOverLocationKey = null;
    },
    canMoveItem(item, direction) {
      if (!this.arrangeMode) return false;
      const locationItems = this.locationItems(item);
      const index = locationItems.findIndex(candidate => Number(candidate.id) === Number(item.id));
      const nextIndex = index + direction;
      return index >= 0 && nextIndex >= 0 && nextIndex < locationItems.length;
    },
    moveItem(item, direction) {
      const locationItems = this.locationItems(item);
      const index = locationItems.findIndex(candidate => Number(candidate.id) === Number(item.id));
      const targetIndex = direction < 0 ? 0 : locationItems.length - 1;
      if (index < 0 || index === targetIndex) return;
      const [movedItem] = locationItems.splice(index, 1);
      locationItems.splice(targetIndex, 0, movedItem);
      this.normalizeLocationOrder(locationItems);
    },
    startDrag(event, item) {
      if (!this.arrangeMode) return;
      this.draggedItemId = item.id;
      this.dragOverItemId = null;
      this.dragOverLocationKey = null;
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(item.id));
      }
    },
    locationKey(locationId) {
      return locationId === null || locationId === undefined ? 'none' : String(locationId);
    },
    autoScrollDuringDrag(event) {
      const edgeSize = 90;
      const speed = 18;
      if (event.clientY < edgeSize) window.scrollBy(0, -speed);
      else if (event.clientY > window.innerHeight - edgeSize) window.scrollBy(0, speed);
    },
    handleItemDragOver(event, item) {
      if (!this.arrangeMode || !this.draggedItemId) return;
      this.autoScrollDuringDrag(event);
      this.dragOverItemId = item.id;
      this.dragOverLocationKey = this.locationKey(item.location_id);
    },
    handleLocationDragOver(event, locationId) {
      if (!this.arrangeMode || !this.draggedItemId) return;
      this.autoScrollDuringDrag(event);
      this.dragOverItemId = null;
      this.dragOverLocationKey = this.locationKey(locationId);
    },
    setItemLocation(item, locationId) {
      const normalizedLocationId = locationId === null || locationId === undefined ? null : Number(locationId);
      const location = normalizedLocationId === null
        ? null
        : this.locations.find(candidate => Number(candidate.id) === normalizedLocationId);
      item.location_id = normalizedLocationId;
      item.location_name = location?.name || null;
    },
    dropItem(targetItem) {
      const draggedItem = this.items.find(item => Number(item.id) === Number(this.draggedItemId));
      if (!draggedItem) {
        this.endDrag();
        return;
      }
      const sourceItems = this.locationItems(draggedItem)
        .filter(item => Number(item.id) !== Number(draggedItem.id));
      const destinationItems = this.locationItems(targetItem)
        .filter(item => Number(item.id) !== Number(draggedItem.id));
      const targetIndex = destinationItems.findIndex(item => Number(item.id) === Number(targetItem.id));
      this.setItemLocation(draggedItem, targetItem.location_id);
      destinationItems.splice(Math.max(0, targetIndex), 0, draggedItem);
      this.normalizeLocationOrder(sourceItems);
      this.normalizeLocationOrder(destinationItems);
      this.endDrag();
    },
    dropOnLocation(locationId) {
      const draggedItem = this.items.find(item => Number(item.id) === Number(this.draggedItemId));
      if (!draggedItem) return this.endDrag();
      const sourceItems = this.locationItems(draggedItem)
        .filter(item => Number(item.id) !== Number(draggedItem.id));
      const destinationItems = this.items
        .filter(item => this.sameLocation(item.location_id, locationId) && Number(item.id) !== Number(draggedItem.id))
        .sort((left, right) => Number(left.display_order || 0) - Number(right.display_order || 0));
      this.setItemLocation(draggedItem, locationId);
      destinationItems.unshift(draggedItem);
      this.normalizeLocationOrder(sourceItems);
      this.normalizeLocationOrder(destinationItems);
      this.endDrag();
    },
    endDrag() {
      this.draggedItemId = null;
      this.dragOverItemId = null;
      this.dragOverLocationKey = null;
    },
    cancelArrange() {
      this.items.forEach(item => {
        if (Object.prototype.hasOwnProperty.call(this.arrangeSnapshot, item.id)) {
          const snapshot = this.arrangeSnapshot[item.id];
          item.display_order = snapshot.displayOrder;
          item.location_id = snapshot.locationId;
          item.location_name = snapshot.locationName;
        }
      });
      this.arrangeMode = false;
      this.arrangeSnapshot = {};
      this.endDrag();
    },
    async saveItemOrder() {
      const groupsByLocation = new Map();
      this.items.forEach(item => {
        const key = item.location_id === null || item.location_id === undefined ? 'none' : String(item.location_id);
        if (!groupsByLocation.has(key)) groupsByLocation.set(key, []);
        groupsByLocation.get(key).push(item);
      });
      const groups = [...groupsByLocation.values()].map(groupItems => ({
        locationId: groupItems[0].location_id ?? null,
        itemIds: groupItems
          .sort((left, right) => Number(left.display_order || 0) - Number(right.display_order || 0))
          .map(item => Number(item.id)),
      }));

      this.savingOrder = true;
      try {
        const response = await secureAxios.put('/api/inventory/items/display-order', { groups });
        this.arrangeMode = false;
        this.arrangeSnapshot = {};
        this.notify(response.data?.message || 'Physical item order saved', 'success');
        await this.loadInventory();
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to save item order', 'error');
      } finally {
        this.savingOrder = false;
      }
    },
    async openItemDialog() {
      this.editingItemId = null;
      this.itemForm = emptyItemForm();
      this.itemDialog = true;
      await this.loadRestockOptions();
    },
    openEditDialog(item) {
      this.editingItemId = Number(item.id);
      this.itemForm = {
        name: item.name || '',
        description: item.description || '',
        categoryId: item.category_id || null,
        locationId: item.location_id || null,
        openingQuantity: 0,
        unitCost: Number(item.average_unit_cost || 0),
        sellingPrice: Number(item.default_selling_price || 0),
        minimumStock: item.minimum_stock === null ? null : Number(item.minimum_stock),
        reorderQuantity: item.reorder_quantity === null ? null : Number(item.reorder_quantity),
      };
      this.itemDialog = true;
    },
    normalizeItemName(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');
    },
    async switchAddToRestock() {
      const itemId = Number(this.selectedExistingAddItem?.id);
      if (!itemId) return;
      this.itemDialog = false;
      await this.openRestockDialog();
      this.restockForm.itemId = itemId;
      this.handleRestockItemChange(itemId);
    },
    async saveItem() {
      if (!String(this.itemForm.name || '').trim()) {
        return this.notify('Item name is required', 'warning');
      }
      this.saving = true;
      try {
        if (this.editingItemId) {
          await secureAxios.put(`/api/inventory/items/${this.editingItemId}`, this.itemForm);
        } else {
          await secureAxios.post('/api/inventory/items', this.itemForm);
        }
        this.itemDialog = false;
        this.notify(this.editingItemId ? 'Inventory item updated' : 'Inventory item added', 'success');
        await this.loadInventory();
        this.activeTab = 'items';
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to save inventory item', 'error');
      } finally {
        this.saving = false;
      }
    },
    async deleteItem() {
      if (!this.editingItemId) return;

      const itemId = Number(this.editingItemId);
      const itemName = this.itemForm.name.trim() || 'this item';
      const confirmed = window.confirm(
        `Delete ${itemName} from this studio's inventory?\n\nIt will disappear from Items and future counts. Existing count and movement history will be preserved.`
      );
      if (!confirmed) return;

      this.deletingItem = true;
      try {
        const response = await secureAxios.delete(`/api/inventory/items/${itemId}`);
        this.itemDialog = false;
        this.editingItemId = null;
        this.notify(response.data?.message || 'Inventory item removed', 'success');
        await this.loadInventory();
        this.workflowRefreshKey += 1;
        this.activeTab = 'items';
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to remove inventory item', 'error');
      } finally {
        this.deletingItem = false;
      }
    },
    openStockDialog(item) {
      this.stockForm = {
        itemId: Number(item.id),
        itemName: item.name,
        currentQuantity: Number(item.quantity_on_hand || 0),
        quantity: Number(item.quantity_on_hand || 0),
        reason: 'Manual stock adjustment',
        notes: '',
      };
      this.stockDialog = true;
    },
    async saveStockAdjustment() {
      if (!Number.isInteger(Number(this.stockForm.quantity)) || Number(this.stockForm.quantity) < 0) {
        return this.notify('Enter a valid stock quantity', 'warning');
      }
      this.savingStock = true;
      try {
        const response = await secureAxios.post(`/api/inventory/items/${this.stockForm.itemId}/adjust-stock`, {
          quantity: Number(this.stockForm.quantity),
          reason: this.stockForm.reason,
          notes: this.stockForm.notes,
        });
        this.stockDialog = false;
        this.notify(response.data?.message || 'Stock quantity adjusted', 'success');
        await this.loadInventory();
        this.workflowRefreshKey += 1;
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to adjust stock', 'error');
      } finally {
        this.savingStock = false;
      }
    },
    inventoryDateOnly(value) {
      if (!value) return null;
      const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
      return year && month && day ? new Date(year, month - 1, day) : null;
    },
    currentInventoryPeriod() {
      if (!this.overviewPeriods.length) return null;
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const ordered = [...this.overviewPeriods].sort((left, right) => (
        this.inventoryDateOnly(left.start_date) - this.inventoryDateOnly(right.start_date)
      ));
      return ordered.find((period, index) => {
        const start = this.inventoryDateOnly(period.start_date);
        const nextStart = this.inventoryDateOnly(ordered[index + 1]?.start_date);
        return start && today >= start && (!nextStart || today < nextStart);
      }) || ordered.find(period => this.inventoryDateOnly(period.start_date) >= today)
        || ordered[ordered.length - 1];
    },
    async loadOverviewRecentSales() {
      this.overviewSalesLoading = true;
      try {
        const response = await secureAxios.get('/api/inventory/sales-history', {
          params: { period_id: this.overviewSalesPeriodId || undefined },
        });
        this.overview.recentSales = (response.data?.rows || []).slice(0, 8);
        this.overviewSalesSummary = response.data?.summary
          || { sales: 0, cost: 0, profit: 0, quantity: 0, missingCostSales: 0 };
      } catch (error) {
        this.error = error.response?.data?.message || 'Unable to load recent sales.';
        this.overview.recentSales = [];
        this.overviewSalesSummary = { sales: 0, cost: 0, profit: 0, quantity: 0, missingCostSales: 0 };
      } finally {
        this.overviewSalesLoading = false;
      }
    },
    async loadRestockOptions() {
      this.loadingRestockOptions = true;
      try {
        const response = await secureAxios.get('/api/inventory/restock-options');
        this.restockOptions = response.data || [];
        return true;
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to load existing inventory items', 'error');
        return false;
      } finally {
        this.loadingRestockOptions = false;
      }
    },
    async openRestockDialog() {
      this.editingRestockMovementId = null;
      this.restockQuantityEditable = true;
      this.restockForm = { itemId: null, receivedQuantity: 1, unitCost: '', sellingPrice: '', notes: '' };
      this.restockDialog = true;
      const loaded = await this.loadRestockOptions();
      if (!loaded) {
        this.restockDialog = false;
      }
    },
    async openEditRestockDialog(movement) {
      this.editingRestockMovementId = Number(movement?.id) || null;
      this.restockQuantityEditable = Number(movement?.quantity_editable) === 1;
      this.restockDialog = true;
      const loaded = await this.loadRestockOptions();
      if (!loaded || !this.editingRestockMovementId) {
        this.closeRestockDialog();
        return;
      }
      this.restockForm = {
        itemId: Number(movement.item_id),
        receivedQuantity: Number(movement.quantity_change || 1),
        unitCost: movement.unit_cost === null || movement.unit_cost === undefined ? '' : Number(movement.unit_cost),
        sellingPrice: movement.unit_price === null || movement.unit_price === undefined ? '' : Number(movement.unit_price),
        notes: movement.notes || '',
      };
    },
    closeRestockDialog() {
      this.restockDialog = false;
      this.editingRestockMovementId = null;
      this.restockQuantityEditable = true;
    },
    handleRestockItemChange(itemId) {
      const item = this.restockOptions.find(option => Number(option.id) === Number(itemId));
      this.restockForm.unitCost = '';
      this.restockForm.sellingPrice = item ? Number(item.default_selling_price || 0) : '';
    },
    isRestockItemActive(item) {
      return Number(item?.item_active) === 1 && Number(item?.studio_item_active) === 1;
    },
    restockOptionSubtitle(item) {
      let status = `${Number(item.quantity_on_hand || 0)} in stock`;
      if (!item.studio_item_id) status = 'Not yet stocked in this studio';
      else if (!this.isRestockItemActive(item)) status = 'Archived';
      return item.location_name ? `${status} · ${item.location_name}` : status;
    },
    async saveRestock() {
      if (!this.selectedRestockItem) return this.notify('Choose an existing inventory item', 'warning');
      if (!Number.isInteger(Number(this.restockForm.receivedQuantity)) || Number(this.restockForm.receivedQuantity) < 1) {
        return this.notify('Enter a received quantity of at least 1', 'warning');
      }
      this.savingRestock = true;
      try {
        const payload = {
          receivedQuantity: Number(this.restockForm.receivedQuantity),
          unitCost: this.restockForm.unitCost,
          sellingPrice: this.restockForm.sellingPrice,
          notes: this.restockForm.notes,
        };
        const response = this.editingRestockMovementId
          ? await secureAxios.put(`/api/inventory/movements/${this.editingRestockMovementId}/restock`, payload)
          : await secureAxios.post(`/api/inventory/items/${this.restockForm.itemId}/restock`, payload);
        const wasEditing = Boolean(this.editingRestockMovementId);
        this.closeRestockDialog();
        this.notify(response.data?.message || 'Item restocked', 'success');
        await this.loadInventory();
        this.workflowRefreshKey += 1;
        this.activeTab = wasEditing ? 'history' : 'items';
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to restock item', 'error');
      } finally {
        this.savingRestock = false;
      }
    },
    openLookupDialog(type) {
      this.lookupType = type;
      this.lookupName = '';
      this.lookupDialog = true;
    },
    async saveLookup() {
      const name = this.lookupName.trim();
      if (!name) return;
      this.savingLookup = true;
      try {
        const endpoint = this.lookupType === 'category' ? 'categories' : 'locations';
        const response = await secureAxios.post(`/api/inventory/${endpoint}`, { name });
        if (this.lookupType === 'category') {
          this.categories.push(response.data);
          this.itemForm.categoryId = response.data.id;
        } else {
          this.locations.push(response.data);
          this.itemForm.locationId = response.data.id;
        }
        this.lookupDialog = false;
        this.notify(`${this.lookupType === 'category' ? 'Category' : 'Location'} added`, 'success');
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to save', 'error');
      } finally {
        this.savingLookup = false;
      }
    },
    isLowStock(item) {
      return Number(item.quantity_on_hand) < 0
        || (item.minimum_stock !== null && Number(item.quantity_on_hand) <= Number(item.minimum_stock));
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        maximumFractionDigits: 0,
      }).format(Number(value || 0));
    },
    recentSaleHasCost(sale) {
      return Number(sale.unit_cost || 0) > 0;
    },
    recentSaleQuantity(sale) {
      return Math.max(0, Number(sale.quantity ?? Math.abs(Number(sale.quantity_change || 0))));
    },
    recentSaleCost(sale) {
      return this.recentSaleQuantity(sale) * Number(sale.unit_cost || 0);
    },
    recentSaleProfit(sale) {
      return Number(sale.sale_total || 0) - this.recentSaleCost(sale);
    },
    formatMovementType(value) {
      return String(value || '').replaceAll('_', ' ').replace(/\b\w/g, letter => letter.toUpperCase());
    },
    goToOrders() {
      this.activeTab = 'orders';
      this.notify('Create a new order and add the low-stock item.', 'primary');
    },
    goToCounts() {
      this.activeTab = 'counts';
      this.notify('Review the latest physical count and missing stock movements.', 'warning');
    },
    async handleWorkflowChange() {
      await this.loadInventory();
      this.workflowRefreshKey += 1;
    },
    notify(message, color = 'primary') {
      this.snackbar = { show: true, message, color };
    },
  },
};
</script>

<style scoped>
.inventory-page {
  max-width: 1360px;
  margin: 0 auto;
  padding: 28px 28px 60px;
  color: #203247;
}

.inventory-page__heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 18px;
}

.inventory-page__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.inventory-page__eyebrow {
  margin-bottom: 3px;
  color: #6b7f93;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.15em;
}

.inventory-page h1 {
  margin: 0;
  color: #0a2647;
  font-size: 30px;
  line-height: 1.2;
}

.inventory-page p {
  margin: 4px 0 0;
  color: #6b7f93;
  font-size: 13px;
}

.inventory-tabs {
  border-bottom: 1px solid #dfe7f0;
}

.inventory-window {
  padding-top: 20px;
}

.summary-card,
.panel-card {
  border: 1px solid #dfe7f0;
  border-radius: 10px;
  background: #fff;
}

.summary-card {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 116px;
  padding: 18px;
}

.summary-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex: 0 0 auto;
  border-radius: 10px;
  background: #eef5fb;
  color: #2c74b3;
}

.summary-card__label {
  color: #6b7f93;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.summary-card__value {
  margin-top: 2px;
  color: #0a2647;
  font-size: 23px;
  font-weight: 700;
}

.summary-card__helper {
  color: #7d8b99;
  font-size: 11px;
}

.attention-clear {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 11px 16px;
  border: 1px solid #cce8da;
  border-radius: 9px;
  background: #f3fbf7;
  color: #2f5f49;
  font-size: 12px;
}

.attention-clear span {
  color: #6b7f76;
}

.panel-card {
  min-height: 100%;
  overflow: hidden;
}

.panel-card__heading,
.items-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 20px 22px;
  border-bottom: 1px solid #edf1f5;
}

.panel-card h2,
.items-toolbar h2 {
  margin: 0;
  color: #173553;
  font-size: 17px;
}

.attention-list,
.movement-list {
  padding: 4px 22px;
}

.recent-sales-table-wrap {
  overflow-x: auto;
  padding: 0 22px 4px;
}

.recent-sales-columns,
.recent-sale-row,
.recent-sales-total {
  display: grid;
  min-width: 720px;
  grid-template-columns: minmax(120px, 1.2fr) minmax(210px, 2fr) 46px minmax(90px, .8fr) minmax(90px, .8fr) minmax(90px, .8fr);
  gap: 12px;
  align-items: center;
}

.recent-sales-grid--no-cost {
  min-width: 520px;
  grid-template-columns: minmax(120px, 1.2fr) minmax(210px, 2fr) 46px minmax(90px, .8fr);
}

.recent-sales-columns {
  padding: 10px 0 6px;
  color: #7b8998;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .05em;
  text-transform: uppercase;
}

.recent-sales-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.recent-sales-period {
  width: 210px;
  flex: 0 0 210px;
}

.attention-row,
.movement-row,
.recent-sale-row {
  min-height: 66px;
  border-bottom: 1px solid #edf1f5;
}

.attention-row:last-child,
.movement-row:last-child,
.recent-sale-row:last-child {
  border-bottom: 0;
}

.recent-sale-row__buyer,
.recent-sale-row__item {
  min-width: 0;
  overflow: hidden;
  color: #203247;
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recent-sale-row__quantity {
  color: #52677c;
  font-weight: 700;
  text-align: right;
}

.recent-sale-row__money {
  color: #40536a;
  text-align: right;
}

.recent-sale-row__profit {
  color: #27845b;
}

.recent-sales-total {
  min-height: 48px;
  border-top: 1px solid #d9e2ea;
  color: #173553;
  font-size: 12px;
}

.attention-row,
.movement-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.attention-row__indicator {
  width: 7px;
  height: 7px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: #f0a227;
}

.attention-row__content,
.movement-row > div:first-child {
  display: flex;
  flex: 1;
  flex-direction: column;
}

.attention-row span,
.movement-row span,
.attention-row__stock span,
.items-table td span {
  color: #7d8b99;
  font-size: 11px;
}

.attention-row__stock {
  display: flex;
  min-width: 84px;
  flex-direction: column;
  text-align: right;
}

.movement-row__quantity {
  font-size: 14px;
  font-weight: 700;
}

.movement-row__quantity.positive { color: #27845b; }
.movement-row__quantity.negative { color: #c34d4d; }

.empty-panel {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 190px;
  padding: 30px;
}

.empty-panel--compact { min-height: 150px; }
.empty-panel p { margin-top: 3px; }

.items-search {
  max-width: 360px;
}

.items-toolbar__controls {
  display: flex;
  width: min(100%, 820px);
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.items-group-select {
  max-width: 180px;
}

.items-table-wrap {
  overflow-x: auto;
}

.items-table {
  width: 100%;
  border-collapse: collapse;
}

.items-table th,
.items-table td {
  padding: 13px 16px;
  border-bottom: 1px solid #edf1f5;
  color: #40536a;
  font-size: 12px;
  text-align: left;
  white-space: nowrap;
}

.items-table th {
  background: #3f454b;
  color: #ffffff;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.items-table tbody tr:not(.items-table__group-row) {
  background: #ffffff;
}

.items-table tbody tr.items-table__item-row--alternate {
  background: #f0f4f8;
}

.items-table tbody tr:not(.items-table__group-row) td {
  padding-top: 9px;
  padding-bottom: 9px;
}

.table-sort {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  font-weight: 700;
  letter-spacing: inherit;
  text-transform: inherit;
}

.table-sort:hover {
  color: #2c74b3;
}

.items-table__group-row td {
  display: table-cell !important;
  min-width: 0 !important;
  padding: 10px 16px;
  border-bottom-color: #cbd1d5;
  background: #dfe3e6;
  color: #35414b;
}

.items-table__group-row td.items-table__group-drop-target {
  box-shadow: inset 0 0 0 2px #2c74b3;
  background: #d8eaf7;
}

.items-table__group-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.items-table__group-label strong {
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.items-table__group-label span {
  margin-left: auto;
  color: #62809a;
  font-size: 11px;
}

.items-table td:first-child {
  display: table-cell;
  min-width: 190px;
}

.location-order-actions {
  display: inline-flex;
  align-items: center;
  gap: 1px;
}

.items-table td:first-child > strong,
.items-table td:first-child > span {
  display: block;
  line-height: 1.25;
}

.items-table td:first-child > span {
  margin-top: 2px;
}

.items-table .number-cell { text-align: right; }
.items-table .stock-cell { text-align: center; }
.items-table__empty { height: 140px; text-align: center !important; }

.item-actions {
  display: flex;
  gap: 2px;
}

.item-actions--arrange {
  align-items: center;
}

.drag-handle {
  color: #6b7f93;
  cursor: grab;
}

.items-table__item-row--arranging {
  cursor: grab;
  user-select: none;
}

.items-table__item-row--dragging {
  opacity: 0.45;
}

.items-table__item-row--drag-over td {
  border-top: 2px solid #2c74b3;
}

.stock-dialog__item {
  display: flex;
  flex-direction: column;
  margin-bottom: 18px;
  color: #173553;
}

.stock-dialog__item span {
  margin-top: 2px;
  color: #6b7f93;
  font-size: 12px;
}

.dialog-title {
  color: #0a2647;
  font-size: 18px;
  font-weight: 700;
}

.dialog-quick-links {
  display: flex;
  gap: 8px;
  margin-top: -6px;
}

.inventory-floating-save {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 20;
  font-weight: 800;
  box-shadow: 0 6px 18px rgba(19, 63, 101, 0.3);
}

@media (max-width: 700px) {
  .inventory-page { padding: 20px 14px 50px; }
  .inventory-page__heading { align-items: stretch; flex-direction: column; }
  .inventory-page__actions { align-items: stretch; flex-direction: column; }
  .inventory-page__actions .v-btn { align-self: stretch; }
  .items-toolbar { align-items: stretch; flex-direction: column; }
  .items-toolbar__controls { width: 100%; align-items: stretch; flex-direction: column; }
  .items-group-select { max-width: none; }
  .items-search { max-width: none; }
  .attention-row { align-items: flex-start; flex-wrap: wrap; padding: 12px 0; }
  .attention-row__content { min-width: calc(100% - 24px); }
  .attention-row__stock { text-align: left; }
  .inventory-floating-save { right: 14px; bottom: 14px; }
}
</style>
